import { useMutation, useQuery } from '@apollo/client';
import { Icon } from '@mui/material';
import Card from '@mui/material/Card';
import Container from '@mui/material/Container';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import LinearProgress from '@mui/material/LinearProgress';
import MenuItem from '@mui/material/MenuItem';
import Select from '@mui/material/Select';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { FormSubmitRow } from 'components/common/form/submit-row';
import { getCallCenterSummaryText } from 'components/views/company/call-centers/summary-text';
import { FormTextField } from 'components/views/forms/form-text-field';
import { useSnackbar } from 'providers/snackbar';
import { FC, useMemo } from 'react';
import { Controller, ControllerRenderProps, FormProvider, useForm } from 'react-hook-form';
import { Link, useParams } from 'react-router';
import routes from 'routes';
import {
  CampaignCallCenter as ICampaignCallCenter,
  CampaignCallCenterVariables,
} from 'types/apollo/CampaignCallCenter';
import { CampaignDidUpdate, CampaignDidUpdateVariables } from 'types/apollo/CampaignDidUpdate';

import { CampaignCallCenterQuery } from './campaign-call-center.graphql';
import { CampaignDidUpdateMutation } from './campaign-did-update.graphql';

interface FormData {
  callCenter: string;
  didInbound: string;
  didOutbound: string;
  didScheduledOutbound: string;
}

const phoneRegExp = /^\+\d{11}$/g;
const EMPTY_FORM: FormData = {
  callCenter: '',
  didInbound: '',
  didOutbound: '',
  didScheduledOutbound: '',
};

export const CampaignCallCenter: FC = () => {
  const { campaignId: id } = useParams();
  const { openSnackbar } = useSnackbar();

  const { data, loading, refetch } = useQuery<ICampaignCallCenter, CampaignCallCenterVariables>(
    CampaignCallCenterQuery,
    {
      variables: { campaignId: id ?? '0' },
      fetchPolicy: 'cache-and-network',
      notifyOnNetworkStatusChange: false,
    },
  );

  const values = useMemo<FormData>(() => {
    const config = data?.campaign?.callCenterConfiguration;
    if (!config) return EMPTY_FORM;
    return {
      callCenter: config.callCenter?.id ?? '',
      didInbound: config.didInbound ?? '',
      didOutbound: config.didOutbound ?? '',
      didScheduledOutbound: config.didScheduledOutbound ?? '',
    };
  }, [data]);

  const form = useForm<FormData>({
    values,
    mode: 'all',
  });
  const { control } = form;

  const callCenters = data?.campaign?.brand?.company?.callCenters ?? [];
  const companyId = data?.campaign?.companyId != null ? String(data.campaign.companyId) : undefined;

  const [updateCampaignDid] = useMutation<CampaignDidUpdate, CampaignDidUpdateVariables>(
    CampaignDidUpdateMutation,
  );

  const onSubmit = form.handleSubmit(async (input) => {
    if (!data?.campaign?.id) return;
    try {
      // Only send callCenterId when the user actually changed it — the DID-only
      // edit path is hot, and the server's same-company guard would otherwise
      // do a wasted call-center lookup on every save.
      const callCenterChanged = input.callCenter !== values.callCenter;
      await updateCampaignDid({
        variables: {
          input: {
            campaignId: data.campaign.id,
            didInbound: input.didInbound,
            didOutbound: input.didOutbound || null,
            didScheduledOutbound: input.didScheduledOutbound || null,
            ...(callCenterChanged && { callCenterId: input.callCenter || null }),
          },
        },
      });
      await refetch();
      openSnackbar({ message: 'Call center connection saved', severity: 'success' });
    } catch {
      // Surfaced by GraphqlWrapper.
    }
  });

  const renderCallCenterField = ({
    field,
  }: {
    field: ControllerRenderProps<FormData, 'callCenter'>;
  }) => (
    <FormControl variant="standard" sx={{ m: 1, minWidth: 120 }}>
      <InputLabel id="callCenter">
        Call Center{' '}
        {companyId && field.value && (
          <Link
            to={routes.company.callCenter.resolve({ id: companyId, callCenterId: field.value })}
          >
            <Icon>open_in_new</Icon>
          </Link>
        )}
      </InputLabel>
      <Select labelId="callCenter" id="callCenter" label="Call Center" {...field}>
        {callCenters.map((c) => (
          <MenuItem key={c.id} value={c.id}>
            <Typography display="inline" component="span" fontWeight="bold">
              {c.id}
            </Typography>
            &nbsp;{getCallCenterSummaryText(c)}
            ,&nbsp;{c.holidays.length} holiday(s)
          </MenuItem>
        ))}
      </Select>
    </FormControl>
  );

  const hasData = !!data?.campaign;

  return (
    <Container maxWidth="lg" sx={{ p: { xs: 2, md: 3 } }}>
      <form onSubmit={onSubmit}>
        <Card variant="outlined" sx={{ padding: '16px' }}>
          <Stack spacing={2}>
            <Typography variant="h5">Connection</Typography>
            {!hasData && loading && <LinearProgress sx={{ height: '2px', mt: 1 }} />}
            {hasData && (
              <>
                <Controller
                  name="callCenter"
                  control={control}
                  render={({ field }) => renderCallCenterField({ field })}
                />
                <FormTextField
                  form={form}
                  name="didInbound"
                  label="Inbound DID"
                  variant="standard"
                  customValidate={phoneRegExp}
                  customValidateMessage="Must be properly formatted phone like +12345554321"
                />
                <FormTextField
                  form={form}
                  name="didOutbound"
                  label="Outbound DID"
                  variant="standard"
                  customValidate={phoneRegExp}
                  customValidateMessage="Must be properly formatted phone like +12345554321"
                />
                <FormTextField
                  form={form}
                  optional
                  name="didScheduledOutbound"
                  label="Scheduled Outbound DID"
                  variant="standard"
                  customValidate={phoneRegExp}
                  customValidateMessage="Must be properly formatted phone like +12345554321"
                />
              </>
            )}
          </Stack>
        </Card>
        {hasData && (
          <FormProvider {...form}>
            <FormSubmitRow sx={{ marginTop: 2 }} onCancel={() => form.reset(values)} />
          </FormProvider>
        )}
      </form>
    </Container>
  );
};
